import { FC, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import Modal from "Components/UI/Modal";
import Button from "Components/UI/Button";
import { Icon } from "Components/UI";
import { Switch } from "antd";
import SpinCustom from "Components/UI/SpinCustom";
import Tooltip from "Components/UI/Tooltip";
import { copyToClipboard } from "Utils/TextUtils";
import { Mixpanel } from 'Utils/MixPanel';
import { useTimeout } from "Context/TimeoutContext";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";
import { getUserInfo } from "Store/Auth/Auth.selector";
import { getCurrentTenant, getTenants } from "Store/Tenant/Tenant.selector";
import {
  FooterButtons,
  FooterHr,
  Header,
  InfoCard,
  LinkCard,
  LoadingWrapper,
  SwitchCard,
  Wrapper,
} from "./styles";

interface IShareLink {
  visible: boolean;
  constructionSiteId: number;
  context?: number;
  shareLink?: string;
  loading?: boolean;
  onCancel: () => void;
}

const ShareLink: FC<IShareLink> = ({
  visible,
  constructionSiteId,
  loading,
  context,
  shareLink,
  onCancel,
}) => {
  const { t } = useTranslation('activities');
  const location = useLocation();
  const { set, clear } = useTimeout();

  const userInfo = useSelector(getUserInfo);
  const currentTenant = useSelector(getCurrentTenant);
  const tenants = useSelector(getTenants);

  const [shareShortlyLink, setShareShortlyLink] = useState(false);
  const [linkCopy, setLinkCopy] = useState(false);
  
  const defaultLnk = `${window.location.origin}/construtora/${context}?redirectUrl=${encodeURIComponent(`${location.pathname}${location.search}`)}`;

  const handleCurrentShareLink = () => {
    setShareShortlyLink(!shareShortlyLink);
  }

  const handleCopyCurrentShareLink = () => {
    if (shareShortlyLink && shareLink) {
      copyToClipboard(shareLink);
      setLinkCopy(true);

    } else {
      copyToClipboard(defaultLnk);
      setLinkCopy(true);
    }

    Mixpanel.track({
      name: 'ACTIVITY_SHARE_LINK',
      props: {
        constructionSiteId: constructionSiteId,
        withFilter: !!shareShortlyLink && !!shareLink,
      },
      userInfo: userInfo,
      currentListTenant: currentTenant,
      tenantIds: tenants && tenants.length > 0
        ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
        : undefined,
    });
  }

  useEffect(() => {
    if (shareLink) {
      setShareShortlyLink(true)
    }
  }, [shareLink]);

  useEffect(() => {
    if (linkCopy) {
      clear('ShareLink-linkCopy');
      set('ShareLink-linkCopy', () => {
        setLinkCopy(false);
      }, 1000);
    }

    return () => {
      clear('ShareLink-linkCopy');
    }
  }, [linkCopy, set, clear]);

  const footerConfirmButtons = (
    <FooterButtons>
      <Tooltip
        overlay={
          linkCopy
            ? <label style={{ color: 'green' }}>{t('shareModal.copied')}</label>
            : t('shareModal.copyLink')
        }
        placement="top"
      >
        <Button
          onClick={handleCopyCurrentShareLink}
          className="buttonCopy"
        >
          <Icon
            icon="link"
            className="iconLink"
          />
          {t('shareModal.copyLinkButton')}
        </Button>
      </Tooltip>
      <Button
        type="primary"
        onClick={onCancel}
        className="buttonClose"
      >
        {t('shareModal.done')}
      </Button>
    </FooterButtons>
  );

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footerButtons={!loading ? footerConfirmButtons : null}
      footerActive={false}
      width={400}
      style={{ marginRight: '2px' }}
      maskClosable={false}
    >
      <Wrapper>
        <Header>
          <span className="title">
            {t('shareModal.title')}
          </span>
          <Icon
            icon="cancelar"
            onClick={onCancel}
            className="iconClose"
            customSize={12}
          />
        </Header>
        <hr className="midleHr" />
        {!loading && (
          <>
            <span className="description">
              {t('shareModal.description')}
            </span>
            <LinkCard>
              <span className="shareLink">
                {shareShortlyLink ? (shareLink || '') : defaultLnk}
              </span>
              <Tooltip
                overlay={
                  linkCopy
                    ? <label style={{ color: 'green' }}>{t('shareModal.copied')}</label>
                    : t('shareModal.copyLink')
                }
                placement="top"
              >
                <div
                  className="customButtonCopy"
                  onClick={handleCopyCurrentShareLink}
                >
                  <Icon
                    icon="copiar"
                    className="iconCopy"
                  />
                </div>
              </Tooltip>
            </LinkCard>
            {!!shareLink && (
              <SwitchCard checked={`${shareShortlyLink}`}>
                <Switch
                  size="small"
                  onChange={handleCurrentShareLink}
                  className="antdSwitch"
                  checked={shareShortlyLink}
                />
                <span className="switchText">
                  {t('shareModal.includeFilters')}
                </span>
              </SwitchCard>
            )}
            <span className="subDescription">
              {t('shareModal.accessTitle')}
            </span>
            <InfoCard>
              <span className="leftCard">
                <Icon
                  icon="cadeadoFechado"
                  className="infoIcon"
                />
              </span>
              <span className="rightCard">
                <span className="headerText">
                  {t('shareModal.restrictedTitle')}
                </span>
                <span className="subText">
                  {t('shareModal.restrictedDescription')}
                </span>
              </span>
            </InfoCard>
          </>
        )}
        {loading && (
          <LoadingWrapper>
            <SpinCustom size={32} />
            <span className="loadingText">
              {t('shareModal.loading')}
            </span>
          </LoadingWrapper>
        )}
      </Wrapper>
      {!loading && <FooterHr />}
    </Modal>
  )
}

export default ShareLink;
